import {
  Button,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { type PasswordPolicy } from "@repo/api";
import {
  usePasswordErrors,
  validatePasswordWithPolicy,
} from "../../../../hooks/usePasswordPolicy";
import { validatePasswordMatch } from "../../validation";

interface ForgotPasswordConfirmStepProps {
  username: string;
  onSubmit: (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => void;
  isLoading: boolean;
  error?: string;
  policy?: PasswordPolicy;
}

export default function ForgotPasswordConfirmStep({
  username,
  onSubmit,
  isLoading,
  error,
  policy,
}: ForgotPasswordConfirmStepProps) {
  const intl = useIntl();
  const [localUsername, setLocalUsername] = useState(username);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleConfirmPassword, setVisibleConfirmPassword] = useState(false);
  const [confirmPasswordCritical, setConfirmPasswordCritical] = useState(false);

  const passwordErrors = usePasswordErrors(newPassword, policy);
  const showPasswordErrors =
    newPassword.length > 0 && passwordErrors.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (policy && validatePasswordWithPolicy(newPassword, policy).length > 0)
      return;
    const matchValidation = validatePasswordMatch(newPassword, confirmPassword);
    if (!matchValidation.valid) {
      setConfirmPasswordCritical(true);
      return;
    }
    onSubmit(localUsername, confirmationCode, newPassword);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
      <InputField
        id="username"
        name="username"
        label={
          <Typography kind="label" htmlFor="username">
            <FormattedMessage
              id="9LrXPj"
              defaultMessage="Username"
              description="Username field label on forgot password confirm step"
            />
          </Typography>
        }
        type="text"
        value={localUsername}
        setValue={setLocalUsername}
        placeholder={intl.formatMessage({
          id: "J64KhJ",
          defaultMessage: "Enter your username",
          description:
            "Username field placeholder on forgot password confirm step",
        })}
      />
      <InputField
        id="confirmationCode"
        name="confirmationCode"
        label={
          <Typography kind="label" htmlFor="confirmationCode">
            <FormattedMessage
              id="CUhAN3"
              defaultMessage="Passcode"
              description="Passcode field label on forgot password confirm step"
            />
          </Typography>
        }
        type="text"
        value={confirmationCode}
        setValue={setConfirmationCode}
        placeholder={intl.formatMessage({
          id: "NNIodZ",
          defaultMessage: "Enter the passcode from your email",
          description:
            "Passcode field placeholder on forgot password confirm step",
        })}
      />
      <InputField
        id="newPassword"
        name="newPassword"
        label={
          <Typography kind="label" htmlFor="newPassword">
            <FormattedMessage
              id="5Mg78p"
              defaultMessage="New Password"
              description="New password field label on forgot password confirm step"
            />
          </Typography>
        }
        type={visiblePassword ? "text" : "password"}
        value={newPassword}
        setValue={setNewPassword}
        placeholder={intl.formatMessage({
          id: "sKfk7/",
          defaultMessage: "Enter new password",
          description:
            "New password field placeholder on forgot password confirm step",
        })}
        suffixButtonType="password"
        suffixIsToggle={visiblePassword}
        suffixOnClick={() => setVisiblePassword(!visiblePassword)}
        HelperText={
          showPasswordErrors && (
            <div className="flex gap-2 items-start p-3 rounded bg-[var(--color-background-warning-subtle)] text-[var(--color-foreground-warning-bold)]">
              <Icon
                kind="AlertWarningFilled"
                size="small"
                color="--color-foreground-warning-bold"
              />
              <div className="text-sm">
                <strong>
                  {intl.formatMessage({
                    id: "2ICPrl",
                    defaultMessage: "Password must include:",
                    description: "Password requirements heading",
                  })}
                </strong>
                {passwordErrors.map((err, index) => (
                  <div key={index}>{err}</div>
                ))}
              </div>
            </div>
          )
        }
      />
      <InputField
        id="confirmPassword"
        name="confirmPassword"
        label={
          <Typography kind="label" htmlFor="confirmPassword">
            <FormattedMessage
              id="ofx7LT"
              defaultMessage="Confirm Password"
              description="Confirm password field label on forgot password confirm step"
            />
          </Typography>
        }
        type={visibleConfirmPassword ? "text" : "password"}
        value={confirmPassword}
        setValue={(value) => {
          setConfirmPasswordCritical(false);
          setConfirmPassword(value);
        }}
        placeholder={intl.formatMessage({
          id: "9o8/Af",
          defaultMessage: "Confirm new password",
          description:
            "Confirm password field placeholder on forgot password confirm step",
        })}
        suffixButtonType="password"
        suffixIsToggle={visibleConfirmPassword}
        suffixOnClick={() => setVisibleConfirmPassword(!visibleConfirmPassword)}
        isCritical={confirmPasswordCritical}
        setIsCritical={setConfirmPasswordCritical}
        CriticalHelperText={
          <InlineMessage
            text={intl.formatMessage({
              id: "3voxc7",
              defaultMessage: "Passwords do not match",
              description:
                "Confirm password mismatch error on forgot password confirm step",
            })}
            colorTheme="critical"
            size="small"
            leftIcon={<Icon kind="AlertCriticalFilled" />}
          />
        }
      />
      {error && (
        <InlineMessage
          text={error}
          colorTheme="critical"
          kind="background"
          size="small"
          leftIcon={<Icon kind="AlertCriticalFilled" />}
        />
      )}
      <Button
        type="submit"
        size="large"
        className="w-full justify-center"
        isDisabled={isLoading}
      >
        <FormattedMessage
          id="PUNv0p"
          defaultMessage="Continue"
          description="Continue button on forgot password confirm step"
        />
      </Button>
    </form>
  );
}
